import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';
import type { ClassificationResult, ConstructionClass } from '@/types/planData';
import {
  classify,
  ClassificationOverrideError,
  requiredPersonnelFor,
  type ClassifierAnswers,
} from '@/utils/classificationEngine';

// ---------------------------------------------------------------------------
// ClassificationWizard (blueprint v2 C-2) — Q1-Q10 guided flow per PRD §1.3.
// Renders the recommendation card with numbered citations, required personnel,
// and the manual override control (downgrade blocked without justification —
// client-side; server re-enforces via validateStoredClassification in
// planData.codec.ts, wired at constructionPlan.service.ts save path).
// ---------------------------------------------------------------------------

/** Per-lift geometry snapshot pulled from lift details (Q8 — never re-asked). */
export interface WizardLiftSnapshot {
  name: string;
  grossLoadLbs: number | null;
  loadPositionDeg: number | null;   // θ, vertical load-position angle
  horizontalTagDeg: number | null;  // straight-tag horizontal angle
  attachmentDeltaFt: number | null; // highest attachment − rigging attachment (≤4 ft check)
}

export interface ClassificationWizardProps {
  answers: ClassifierAnswers;
  onAnswersChange: (answers: ClassifierAnswers) => void;
  lifts: WizardLiftSnapshot[];
  result: ClassificationResult | null;
  onResultChange: (result: ClassificationResult | null) => void;
  qpName: string;
}

export function defaultClassifierAnswers(): ClassifierAnswers {
  return {
    q1_involvesStructure: true,
    q2_hoisting: true,
    q3_normalMethods: true,
    q4_attachedToStructure: true,
    q5_attachmentLocation: 'legPanelPointOrShaft',
    q6_exceptionConditions: null,
    q7_grossLoadLbs: null,
    q8_geometry: { loadPositionDeg: null, horizontalTagDeg: null },
    q9_criticalLift: false,
    q10_newStructureConcerns: null,
  };
}

const CLASS_LABELS: Record<ConstructionClass, string> = {
  II: 'Class 2 (II)',
  III: 'Class 3 (III)',
  IV: 'Class 4 (IV)',
};

/** Derive Q7/Q8 from the lift snapshots (Q8 is PULLED from lift details, never re-asked). */
export function deriveGeometryFromLifts(lifts: WizardLiftSnapshot[]): {
  grossLoadLbs: number | null;
  loadPositionDeg: number | null;
  horizontalTagDeg: number | null;
} {
  const weights = lifts.map((l) => l.grossLoadLbs).filter((w): w is number => w !== null);
  const thetas = lifts.map((l) => l.loadPositionDeg).filter((t): t is number => t !== null);
  const tags = lifts.map((l) => l.horizontalTagDeg).filter((t): t is number => t !== null);
  return {
    grossLoadLbs: weights.length ? Math.max(...weights) : null,
    loadPositionDeg: thetas.length ? Math.max(...thetas) : null,
    horizontalTagDeg: tags.length ? Math.max(...tags) : null,
  };
}

function BoolQuestion({
  label,
  hint,
  value,
  onChange,
  yesText = 'Yes',
  noText = 'No',
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
  yesText?: string;
  noText?: string;
}) {
  return (
    <div className="py-2">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {hint && <p className="text-xs text-gray-500 mb-1">{hint}</p>}
      <div className="flex gap-2 mt-1">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-3 py-1 rounded text-sm border ${value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          {yesText}
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-3 py-1 rounded text-sm border ${!value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          {noText}
        </button>
      </div>
    </div>
  );
}

function TriQuestion({
  label,
  hint,
  value,
  onChange,
  yesText = 'Yes — concerns identified',
  noText = 'No concerns',
}: {
  label: string;
  hint?: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  yesText?: string;
  noText?: string;
}) {
  return (
    <div className="py-2">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {hint && <p className="text-xs text-gray-500 mb-1">{hint}</p>}
      <div className="flex gap-2 mt-1">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-3 py-1 rounded text-sm border ${value === true ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          {yesText}
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-3 py-1 rounded text-sm border ${value === false ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          {noText}
        </button>
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`px-3 py-1 rounded text-sm border ${value === null ? 'bg-gray-500 text-white border-gray-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          In question
        </button>
      </div>
    </div>
  );
}

/**
 * Audit #2 (HIGH): re-apply a valid stored override (result.override with a
 * non-empty justification) on top of a freshly recomputed base. The old
 * recompute effect called onResultChange(base) unconditionally, wiping any
 * applied override on every answers/lifts change — the "Preserve an
 * already-applied, valid override" comment was false.
 */
export function reapplyStoredOverride(
  base: ClassificationResult,
  result: ClassificationResult | null
): ClassificationResult {
  const stored = result?.override;
  if (!stored || !stored.justification || !stored.justification.trim()) {
    return base;
  }
  return {
    ...base,
    selected: result!.selected,
    override: stored,
  };
}

export function ClassificationWizard({
  answers,
  onAnswersChange,
  lifts,
  result,
  onResultChange,
  qpName,
}: ClassificationWizardProps) {
  // Form consolidation (blueprint slice 2): direct class PICKER instead of a
  // recommendation + override gate. The engine recommendation is still computed
  // (labeled "typical for these inputs") and seeds the default; the QP's pick
  // is authoritative. Downgrades are passed to the engine with the justification
  // "Selected by QP" so ClassificationOverrideError (API safety net) never fires.
  const [pickedClass, setPickedClass] = useState<ConstructionClass | ''>('');
  const [overrideError, setOverrideError] = useState<string | null>(null);

  const derived = useMemo(() => deriveGeometryFromLifts(lifts), [lifts]);

  const buildLiftDetails = () =>
    lifts.map((l) => ({
      loading: { grossLoadLbs: l.grossLoadLbs ?? undefined },
      geometry: {
        tag: { method: 'straight' as const },
        highestAttachmentFt: l.attachmentDeltaFt ?? undefined,
      },
    }));

  /** Apply the QP's picked class on top of a freshly computed base. */
  const applyPicked = (base: ClassificationResult, picked: ConstructionClass): ClassificationResult => {
    if (picked === base.recommended) return base;
    const isDowngrade =
      ({ II: 2, III: 3, IV: 4 } as Record<ConstructionClass, number>)[picked] <
      ({ II: 2, III: 3, IV: 4 } as Record<ConstructionClass, number>)[base.recommended];
    return classify({
      answers,
      liftDetails: buildLiftDetails(),
      override: {
        selected: picked,
        by: qpName || 'Qualified Person',
        justification: isDowngrade ? 'Selected by QP' : undefined,
      },
    });
  };

  // §4.4.3 weight band — non-blocking note when the picked class sits below
  // the band the gross load implies. Flows into the saved classification record.
  const weightBandNoteFor = (selected: ConstructionClass): string | null => {
    const gross = derived.grossLoadLbs;
    if (gross === null || gross <= 500) return null;
    const order: Record<ConstructionClass, number> = { II: 2, III: 3, IV: 4 };
    const band: ConstructionClass = gross <= 2000 ? 'III' : 'IV';
    return order[selected] < order[band]
      ? `Note: gross load exceeds the Class ${band} band per A10.48-2023 §4.4.3 — class selected by QP.`
      : null;
  };

  // Q7/Q8 are pulled from lift details; keep answers in sync (Q8 never re-asked).
  useEffect(() => {
    const next: ClassifierAnswers = {
      ...answers,
      q7_grossLoadLbs: derived.grossLoadLbs,
      q8_geometry: {
        loadPositionDeg: derived.loadPositionDeg,
        horizontalTagDeg: derived.horizontalTagDeg,
      },
    };
    if (
      next.q7_grossLoadLbs !== answers.q7_grossLoadLbs ||
      next.q8_geometry.loadPositionDeg !== answers.q8_geometry.loadPositionDeg ||
      next.q8_geometry.horizontalTagDeg !== answers.q8_geometry.horizontalTagDeg
    ) {
      onAnswersChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derived.grossLoadLbs, derived.loadPositionDeg, derived.horizontalTagDeg]);

  // Recompute the recommendation whenever answers/lifts change (engine is pure).
  // Audit #2 (HIGH): a stored, valid override is re-applied on top of the fresh
  // base before emitting. Form consolidation: when the QP has picked a class,
  // the pick (not the base recommendation) is emitted — downgrades carry the
  // 'Selected by QP' justification so the engine never throws.
  useEffect(() => {
    try {
      const base = classify({
        answers,
        liftDetails: buildLiftDetails(),
      });
      const stored = reapplyStoredOverride(base, result);
      // QP pick wins over both the fresh base and any stored override. When a
      // stored override already targets the same class (save/reload round-trip),
      // keep its audit record rather than re-stamping the timestamp.
      const effective =
        pickedClass && pickedClass !== base.recommended
          ? stored.override && stored.override.by === (qpName || 'Qualified Person') && stored.selected === pickedClass
            ? stored
            : applyPicked(base, pickedClass)
          : stored;
      onResultChange({
        ...effective,
        weightBandNote: weightBandNoteFor(effective.selected) ?? undefined,
      });
      setOverrideError(null);
    } catch (err) {
      if (err instanceof ClassificationOverrideError) {
        setOverrideError(err.message);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, lifts, pickedClass]);

  const set = (patch: Partial<ClassifierAnswers>) => onAnswersChange({ ...answers, ...patch });

  const showIveWarning =
    answers.q5_attachmentLocation === 'other' ||
    (result?.triggers ?? []).some((t) => t.rule === 'IV(e)' || t.rule === 'IV(e)-exception');

  // Form consolidation: §4.4.4.1(b) four-condition exception checklist renders
  // ONLY for mount / mastPipeRoosterHead / other attachments with Class III picked.
  const show4441bChecklist =
    (answers.q5_attachmentLocation === 'mount' ||
      answers.q5_attachmentLocation === 'mastPipeRoosterHead' ||
      answers.q5_attachmentLocation === 'other') &&
    result?.selected === 'III';

  return (
    <div className="space-y-6" data-testid="classification-wizard">
      {/* Q1-Q10 guided flow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-1">
        <BoolQuestion
          label="Q1. Does the work involve the supporting structure or its foundations at all?"
          value={answers.q1_involvesStructure}
          onChange={(v) => set({ q1_involvesStructure: v })}
        />
        <BoolQuestion
          label="Q2. Will any hoisting be performed?"
          value={answers.q2_hoisting}
          onChange={(v) => set({ q2_hoisting: v })}
        />
        <BoolQuestion
          label="Q3. Does the scope involve normal construction methods that do not affect structural integrity?"
          hint="No ⇒ member removal/replacement/modification, foundation/strength effects, special engineered lift — Class IV (IV(b)/(c)/(d))."
          value={answers.q3_normalMethods}
          onChange={(v) => set({ q3_normalMethods: v })}
        />
        <BoolQuestion
          label="Q4. Is the lift system attached to the structure (vs. a detached device, e.g. crane)?"
          hint="Detached ⇒ Class III (III(d)); IV(e)/(f) geometry triggers are still evaluated."
          value={answers.q4_attachedToStructure}
          onChange={(v) => set({ q4_attachedToStructure: v })}
        />
        {answers.q4_attachedToStructure && (
          <div className="py-2">
            <p className="text-sm font-medium text-gray-900">
              Q5. If attached to the structure — where?
            </p>
            <p className="text-xs text-gray-500 mb-1">
              Around vertical leg(s) at panel points (latticed) or the main pole shaft (monopole)?
              Anywhere else (appurtenance/mount/other) ⇒ Class IV (IV(e)) unless the §4.4.4.1 exception applies.
            </p>
            <select
              value={answers.q5_attachmentLocation}
              onChange={(e) =>
                set({
                  q5_attachmentLocation: e.target.value as ClassifierAnswers['q5_attachmentLocation'],
                })
              }
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="legPanelPointOrShaft">Vertical leg panel points / main pole shaft</option>
              <option value="mount">Antenna mount (T-boom / V-boom on tower face)</option>
              <option value="mastPipeRoosterHead">Mast pipe / rooster head (drop-in gin pole standing on mount)</option>
              <option value="other">Other — appurtenance or other member</option>
            </select>
            {answers.q5_attachmentLocation === 'mount' && (
              <p className="text-xs text-amber-700 mt-1">
                Antenna mount ⇒ Class IV (IV(e)). You may derate to Class III only if ALL §4.4.4.1(b) exception
                conditions are met and documented (≤200 lb, ≤4 ft, good condition, proof test).
              </p>
            )}
            {answers.q5_attachmentLocation === 'mastPipeRoosterHead' && (
              <p className="text-xs text-amber-700 mt-1">
                Rooster head / mast pipe (gin pole) ⇒ Class IV (IV(e)) plus §11.4 gin-pole rules. You may derate to
                Class III only if ALL §4.4.4.1(b) exception conditions are met and documented.
              </p>
            )}
          </div>
        )}
        <BoolQuestion
          label="Q9. Is this a critical lift?"
          hint="Conformara proposed default (conservative): critical lift ⇒ Class IV."
          value={answers.q9_criticalLift}
          onChange={(v) => set({ q9_criticalLift: v })}
        />
        <TriQuestion
          label="Q10. New structure/foundation construction — any strength or stability concerns?"
          hint="Concerns ⇒ IV(c); none ⇒ III(c); in question ⇒ engage QE per ANSI/TIA-322-A."
          value={answers.q10_newStructureConcerns}
          onChange={(v) => {
            set({ q10_newStructureConcerns: v });
            if (v === null) {
              onResultChange(
                result
                  ? {
                      ...result,
                      exception: { ...result.exception, qeEngaged: true },
                    }
                  : result
              );
            }
          }}
        />
        <div className="py-2">
          <p className="text-sm font-medium text-gray-900">Q7/Q8. Gross load &amp; tag/lift geometry</p>
          <p className="text-xs text-gray-500">
            Pulled from the lift details below — never re-asked. Max gross load:{' '}
            {derived.grossLoadLbs !== null ? `${derived.grossLoadLbs.toLocaleString('en-US')} lb` : '—'} ·
            Max θ: {derived.loadPositionDeg !== null ? `${derived.loadPositionDeg.toFixed(1)}°` : '—'} ·
            Max horizontal tag: {derived.horizontalTagDeg !== null ? `${derived.horizontalTagDeg.toFixed(1)}°` : '—'}
          </p>
        </div>
      </div>

      {/* IV(e) appurtenance-redirect warning (Z11 special case — exact copy) */}
      {showIveWarning && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg p-3" role="alert">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            This is a Class IV arrangement (IV(e)) unless you comply with ANSI/ASSP A10.48-2023 §4.4.4.1 —
            all four conditions incl. the §9.6 proof test.
          </p>
        </div>
      )}

      {/* Class picker card (form consolidation) — the QP picks the class directly;
          the engine recommendation is computed internally and shown as guidance. */}
      {result && (
        <div className="border border-gray-300 rounded-lg p-4 bg-white" data-testid="classification-result">
          <fieldset className="border-0 m-0 p-0">
            <legend className="text-sm font-semibold text-gray-900 px-0">
              Construction class (Qualified Person selects)
            </legend>
            <div className="flex flex-wrap gap-4 mt-2" role="radiogroup" aria-label="Construction class">
              {(['II', 'III', 'IV'] as ConstructionClass[]).map((c) => {
                const value = pickedClass || result.recommended;
                return (
                  <label key={c} className="flex items-center gap-2 text-sm text-gray-900">
                    <input
                      type="radio"
                      name="construction-class"
                      value={c}
                      checked={value === c}
                      onChange={() => {
                        setPickedClass(c);
                        setOverrideError(null);
                      }}
                      data-testid={`class-option-${c}`}
                    />
                    {CLASS_LABELS[c]}
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Typical for these inputs: <strong>{CLASS_LABELS[result.recommended]}</strong>
              {pickedClass && pickedClass !== result.recommended && (
                <span className="ml-2 text-gray-500">— you picked a different class; your selection is recorded.</span>
              )}
            </p>
          </fieldset>

          {/* Non-blocking §4.4.3 weight-band note — also flows into the saved plan record. */}
          {result.weightBandNote && (
            <p className="mt-2 text-sm text-gray-500" data-testid="weight-band-note">
              {result.weightBandNote}
            </p>
          )}

          <p className="text-xs text-gray-500 mt-2">
            Classifier inputs — gross load: <strong>{derived.grossLoadLbs ?? '—'} lb</strong>
            {' · '}load position θ: <strong>{answers.q8_geometry.loadPositionDeg ?? '—'}°</strong>
            {' · '}tag angle: <strong>{answers.q8_geometry.horizontalTagDeg ?? '—'}°</strong> (from vertical).
            These come from your Lift System figures — if they don't match what you entered, the lift figures are stale.
          </p>

          {result.triggers.length > 0 && (
            <ol className="list-decimal list-inside mt-3 space-y-1 text-sm text-gray-700">
              {result.triggers.map((t, i) => (
                <li key={i}>
                  <strong>{t.rule}:</strong> {t.detail}
                </li>
              ))}
            </ol>
          )}

          {result.exception.qeEngaged && (
            <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded p-2">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-sm text-red-800">
                Class IV — engage a Qualified Engineer for the ANSI/TIA-322-A strength/stability evaluation.
              </p>
            </div>
          )}

          <div className="mt-3 text-sm text-gray-700">
            <p className="font-semibold">Required personnel:</p>
            <ul className="list-disc list-inside">
              {requiredPersonnelFor(result.selected).map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>

          {overrideError && (
            <p className="mt-2 text-sm text-red-700 flex items-center gap-1" role="alert">
              <AlertTriangle className="h-4 w-4" /> {overrideError}
            </p>
          )}
        </div>
      )}

      {/* §4.4.4.1(b) four-condition exception checklist — ONLY for mount /
          mastPipeRoosterHead / other attachments when Class III is picked. */}
      {show4441bChecklist && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4" data-testid="exception-4441b-checklist">
          <p className="text-sm font-semibold text-blue-900">
            §4.4.4.1(b) exception to Class IV(e) — all four conditions must hold for Class III:
          </p>
          <div className="mt-2 space-y-1">
            {(
              [
                ['goodCondition', 'i. Rigging attachments in good condition, no slippage/damage'],
                ['within4ft', 'ii. Primary/redundant rigging on leg panel points or shaft ≤ 4 ft below highest attachment'],
                ['max200lb', 'iii. Gross load does not exceed 200 lb'],
                ['proofTest', 'iv. Documented proof test per §9.6 completed before active hoisting'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-blue-900">
                <input
                  type="checkbox"
                  checked={answers.q6_exceptionConditions?.[key] ?? false}
                  onChange={(e) =>
                    set({
                      q6_exceptionConditions: {
                        goodCondition: answers.q6_exceptionConditions?.goodCondition ?? false,
                        within4ft: answers.q6_exceptionConditions?.within4ft ?? false,
                        max200lb: answers.q6_exceptionConditions?.max200lb ?? false,
                        proofTest: answers.q6_exceptionConditions?.proofTest ?? false,
                        [key]: e.target.checked,
                      },
                    })
                  }
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {!answers.q1_involvesStructure && (
        <div className="flex items-start gap-2 bg-gray-50 border border-gray-300 rounded-lg p-3">
          <Info className="h-5 w-5 text-gray-500 mt-0.5" />
          <p className="text-sm text-gray-700">
            No plan required by §4.4 — other documentation may apply. Plan creation is still allowed
            (zero-hoist plans supported per §4.4 intro).
          </p>
        </div>
      )}

      {answers.q2_hoisting && derived.grossLoadLbs !== null && derived.grossLoadLbs <= 35 && (
        <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <p className="text-sm text-green-800">
            Handline load ≤ 35 lb — need not be documented (II(c), informational note only).
          </p>
        </div>
      )}
    </div>
  );
}
